import {forwardRef} from "react";
import {colorThemes, elementThemes, subThemes} from "../../css/themes/themes.css";
import cx from "../../utils/cx";
import {css, Text} from "../Box/Box";
import {useReactiveIcon} from "../DSButton/button-helpers";
import {DSCheckboxStyles} from "./DSCheckbox.css";
import {DSRadioStyles as styles} from "./DSRadio.css";

export type DSRadioWithLabelProps = DSRadioProps & {
  label: string;
};

export const DSRadioWithLabel = forwardRef<HTMLButtonElement, DSRadioWithLabelProps>(
  (props, ref) => {
    const {label, ...rest} = props;
    return (
      <label className={css({sp: "8px", display: "flex", align: "start"})} htmlFor={rest.id}>
        <DSRadio {...rest} ref={ref} />
        <Text type="label14" color="primary">
          {label}
        </Text>
      </label>
    );
  }
);

export type DSRadioProps = {
  onChange?: (value: boolean) => unknown;
  hasError?: boolean;
  disabled?: boolean;
  checked?: boolean;
  pending?: boolean;
  id?: string;
  "aria-labelledby"?: string;
};

export const DSRadio = forwardRef<HTMLButtonElement, DSRadioProps>(
  ({onChange, hasError, disabled, checked, pending, ...rest}, ref) => {
    const getClasses = (): string[] => {
      if (disabled) {
        return [elementThemes.neutral, colorThemes.elementThemeBase, css({bgWithOpacity: "0.2"})];
      }
      if (hasError) {
        if (checked) {
          return [elementThemes.alert, colorThemes.elementThemeBase];
        } else {
          return [subThemes.alert];
        }
      }
      if (checked) return [elementThemes.active, colorThemes.elementThemeBase];
      return [];
    };

    const {handleClick, reactiveIcon} = useReactiveIcon({
      iconEl: (
        <div
          className={cx(
            styles.indicator.base,
            checked ? DSCheckboxStyles.icon.checked : DSCheckboxStyles.icon.unchecked,
            disabled ? styles.indicator.disabled : styles.indicator.nonDisabled
          )}
        />
      ),
      onClick: onChange,
      passedState: pending ? "loading" : undefined,
      skipSuccessIcon: true,
    });

    return (
      <button
        type="button"
        onClick={() => handleClick(!checked)}
        className={css({rounded: "full"}, DSCheckboxStyles.base, ...getClasses())}
        disabled={disabled}
        role="radio"
        aria-checked={checked}
        aria-disabled={disabled}
        ref={ref}
        {...rest}
      >
        {reactiveIcon}
      </button>
    );
  }
);
